import { DEFAULT_USER_GLOBAL_PERMISSIONS, DEFAULT_USER_PERMISSIONS } from 'constants/permissions';
import type { UserState } from 'redux/modules/user.types';
import {
  clearTour,
  emulatePermissions,
  emulateRole,
  fetchedUser,
  restartTour,
  savedUser,
  setShowDevTools,
  setUserAccountSettings,
  setUserGlobalPermissions,
  setUserPermissions,
  setUserProperties,
  setUserProperty,
  stopEmulatingPermissions,
  stopEmulatingRole,
  userXhrStatusModule,
} from 'redux/modules/user.types';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { User } from 'types/user';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { deserializeProperties } from 'utils/user';

const bootstrappedUserData: User | undefined = window.BOOTSTRAPPED_DATA
  ? window.BOOTSTRAPPED_DATA.user
  : undefined;

const defaultUserProperties = window.BOOTSTRAPPED_DATA.onboarding_properties || [];

const initialState: UserState = {
  ...bootstrappedUserData!, // NOTE: We're assuming that BOOTSTRAPPED_DATA is always present here!
  properties: deserializeProperties(defaultUserProperties, bootstrappedUserData?.id),
  // Permissions for the authenticated users
  // Ref https://app.clubhouse.io/timelyapp/story/1968/support-large-number-of-projects-users
  accountSettings: {},
  permissions: { ...DEFAULT_USER_PERMISSIONS },
  globalPermissions: { ...DEFAULT_USER_GLOBAL_PERMISSIONS },
  showDevtools: false,
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState<UserState>(initialState)
  .cases([fetchedUser, savedUser], (state, payload): UserState => {
    return {
      ...state,
      ...payload,
    };
  })
  .cases(
    [setUserProperties, setUserProperty],
    (state, payload): UserState => ({
      ...state,
      properties: {
        ...state.properties,
        ...payload,
      },
    })
  )
  .case(
    restartTour,
    (state): UserState => ({
      ...state,
      force_tour: true,
    })
  )
  .case(
    clearTour,
    (state): UserState => ({
      ...state,
      force_tour: false,
    })
  )
  .case(
    setUserAccountSettings,
    (state, payload): UserState => ({
      ...state,
      accountSettings: payload,
    })
  )
  .case(
    setUserPermissions,
    (state, payload): UserState => ({
      ...state,
      permissions: payload,
    })
  )
  .case(
    setUserGlobalPermissions,
    (state, payload): UserState => ({
      ...state,
      globalPermissions: payload,
    })
  )
  .case(
    emulatePermissions,
    (state, payload): UserState => ({
      ...state,
      emulatedPermissions: payload,
    })
  )
  .case(
    stopEmulatingPermissions,
    (state): UserState => ({
      ...state,
      emulatedPermissions: null,
    })
  )
  .case(
    emulateRole,
    (state, payload): UserState => ({
      ...state,
      emulatedRoleId: payload,
    })
  )
  .case(
    stopEmulatingRole,
    (state): UserState => ({
      ...state,
      emulatedRoleId: null,
    })
  )
  .case(
    setShowDevTools,
    (state, payload): UserState => ({
      ...state,
      showDevtools: payload,
    })
  );

export default userXhrStatusModule.withXhrStatusReducer(reducer);
